import { z } from 'zod';
/**
 * Payload shape for `GET /api/locations/:id/summary`.
 *
 * Per-location companion to `/api/dashboard/operator-summary`. Drives
 * the admin's LocationInfoCard side panel — same aggregation pattern
 * scoped to a single location instead of the tenant.
 *
 * Currency: dollars (cents in the DB get converted at the API boundary
 * so the client stays format-free). Energy: kWh. All counts are
 * non-negative integers.
 *
 * `revenueLast24h` keeps the name from `OperatorDashboardData` even
 * though its length varies with `window` (7 or 30 daily buckets for
 * 7d/30d periods, 24 hourly buckets for 24h). Mirroring the field name
 * lets the existing `<RevenueChart>` consumer drop in without prop
 * renaming. The chart's chip row picks how to label time labels based
 * on `window`.
 *
 * `currentRateUsdHr` is intentionally omitted at the per-location
 * scope — drivers don't operate chargers from this surface, and
 * "trailing 60-min rate at one location" is a fuzzy metric that
 * fluctuates wildly with sparse session traffic at a single site.
 * Add it later if a use case appears.
 */
export const LocationSummaryDataSchema = z.object({
    /** Sum of cost.total for sessions ended today at this location.
     *  Dollars (not cents). */
    revenueToday: z.number().nonnegative(),
    /** Sum of energy_kwh for sessions ended in the current calendar
     *  month at this location. */
    energyMtd: z.number().nonnegative(),
    /** Count of chargers at this location that are flagged is_active +
     *  is_online with a recent heartbeat (within HEARTBEAT_CUTOFF_MS). */
    chargersOnline: z.number().int().nonnegative(),
    /** Count of is_active chargers at this location, regardless of
     *  current online status. */
    totalChargers: z.number().int().nonnegative(),
    /** Count of is_active chargers at this location that are offline /
     *  silent past the heartbeat cutoff. Operational proxy for "faulted"
     *  pending a real status field on the Charger model. */
    faultedCount: z.number().int().nonnegative(),
    /** Count of `active` sessions (no ended_at) currently in flight at
     *  this location. */
    activeSessions: z.number().int().nonnegative(),
    /** Count of sessions started today at this location, any status.
     *  Used by the panel's "N active / M today" denominator. */
    totalSessionsToday: z.number().int().nonnegative(),
    /** Count of sessions started inside the active `window` at this
     *  location, any status. Drives the chart footer's "$X · N
     *  sessions" copy alongside the bucket array. Distinct from
     *  totalSessionsToday: today-vs-window-aligned with the chart
     *  period chip, so the footer total matches what the chart
     *  visualises. */
    sessionCountWindow: z.number().int().nonnegative(),
    /** Time-bucketed revenue in dollars, oldest first. Length tracks
     *  `window`: 24 entries for '24h', 7 for '7d', 30 for '30d'. Empty
     *  windows return arrays of zeros (NOT null). Field name kept per
     *  the OperatorDashboardData precedent. */
    revenueLast24h: z.array(z.number().nonnegative()),
    /** Window the buckets cover. Echoes back the request period. */
    window: z.enum(['24h', '7d', '30d']),
});
//# sourceMappingURL=location-summary.js.map